import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import {
  Coins, Landmark, FileText, TrendingDown, Percent, Building2,
  RefreshCw, Loader2, AlertCircle,
} from "lucide-react";
import TrendChart from "@/components/FinanceTrendChart";

const TAX_TREND_SERIES = [
  { key: "grand_total", label: "Total", color: "#d97706", width: 3 },
  { key: "gst", label: "GST", color: "#0284c7" },
  { key: "super", label: "Super", color: "#059669" },
  { key: "payg_withholding", label: "PAYG-W", color: "#8b5cf6" },
  { key: "company_tax", label: "Co. Tax", color: "#dc2626" },
];

const fmtMoney = (v) => new Intl.NumberFormat("en-AU", {
  style: "currency", currency: "AUD", maximumFractionDigits: 0,
}).format(v || 0);
const fmtMoney2 = (v) => new Intl.NumberFormat("en-AU", {
  style: "currency", currency: "AUD", maximumFractionDigits: 2,
}).format(v || 0);
const fmtDateTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return `${d.toLocaleDateString("en-AU")} ${d.toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit" })}`;
};

const BUCKETS = [
  { key: "gst", label: "GST (BAS)", icon: Landmark,
    description: "Net GST collected - GST paid, from your Xero Balance Sheet as of today.",
    cadence: "Quarterly (or monthly)" },
  { key: "payg_withholding", label: "PAYG Withholding", icon: Percent,
    description: "PAYG withheld from wages, remitted at BAS time.",
    cadence: "Same schedule as BAS" },
  { key: "payg_instalments", label: "PAYG Income Tax Instalments", icon: TrendingDown,
    description: "Instalment amounts owed on projected company income tax for the year.",
    cadence: "Quarterly instalment notices" },
  { key: "super", label: "Super Guarantee", icon: Building2,
    description: "Statutory superannuation payable to employees (currently 12%).",
    cadence: "Quarterly — due 28 days after quarter end" },
  { key: "fbt", label: "FBT", icon: FileText,
    description: "Fringe Benefits Tax accrued for the FBT year (1 Apr – 31 Mar).",
    cadence: "Annual — 21 May / 25 June" },
  { key: "company_tax", label: "Company Income Tax", icon: Coins,
    description: "Income tax payable / provision for income tax on the Balance Sheet.",
    cadence: "Annual — 15 May following year" },
];

export default function TaxHoldingsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [expandedTenant, setExpandedTenant] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/finance-holdings/taxes");
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load tax holdings");
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const refresh = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.post("/finance-holdings/taxes/refresh");
      setData(data);
      toast.success("Tax holdings refreshed");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Refresh failed");
    }
    setRefreshing(false);
  };

  const totals = data?.totals || {};
  const tenants = data?.tenants || [];
  const grand = data?.grand_total || 0;

  return (
    <div className="p-6 max-w-7xl space-y-6" data-testid="tax-holdings-page">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="h-12 w-12 grid place-items-center rounded-sm bg-amber-100 text-amber-700">
          <Coins className="h-6 w-6" />
        </div>
        <div className="flex-1 min-w-[280px]">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Finance &amp; Payroll</div>
          <h1 className="font-display font-extrabold text-3xl tracking-tight">Tax Holdings</h1>
          <p className="text-sm text-zinc-500 mt-1 max-w-3xl">
            All tax liabilities we should have cash reserved for — BAS, PAYG withholding,
            PAYG instalments, Super Guarantee, FBT and company tax. Pulled from every
            connected Xero tenant&apos;s Balance Sheet.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {data && (
            <span className="text-[11px] text-zinc-500">
              {data.from_cache ? "Cached" : "Fresh"} · {fmtDateTime(data.created_at)}
            </span>
          )}
          <Button variant="outline" size="sm" onClick={refresh} disabled={refreshing}
            data-testid="tax-holdings-refresh-btn">
            {refreshing
              ? <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              : <RefreshCw className="h-4 w-4 mr-1" />}
            Refresh from Xero
          </Button>
        </div>
      </div>

      {/* Total tax owed */}
      <Card className="border-amber-200 bg-amber-50/30">
        <CardContent className="p-6 flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-[0.18em] text-zinc-500">Total tax owed (cash reserve target)</div>
            <div className="text-4xl font-extrabold text-amber-800 mt-1"
              data-testid="tax-holdings-total">
              {loading && !data ? "…" : fmtMoney(grand)}
            </div>
            <div className="text-xs text-zinc-500 mt-1">
              Across {tenants.length} Xero tenant{tenants.length === 1 ? "" : "s"}. Negative balances = refund due.
            </div>
          </div>
          <Coins className="h-14 w-14 text-amber-300" />
        </CardContent>
      </Card>

      {/* Per-bucket breakdown */}
      <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
        {BUCKETS.map((b) => (
          <Card key={b.key} className="border-zinc-200"
            data-testid={`tax-bucket-${b.key}`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <b.icon className="h-4 w-4 text-amber-600" />
                  <div className="font-semibold text-sm">{b.label}</div>
                </div>
                <div className={`text-lg font-bold ${(totals[b.key] || 0) < 0 ? "text-emerald-700" : "text-zinc-800"}`}>
                  {loading && !data ? "…" : fmtMoney(totals[b.key] || 0)}
                </div>
              </div>
              <p className="text-xs text-zinc-500 leading-relaxed">{b.description}</p>
              <div className="text-[11px] text-zinc-500 mt-2 italic">Due: {b.cadence}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      <TrendChart kind="taxes" days={90} series={TAX_TREND_SERIES} />

      {/* Per-tenant breakdown */}
      {tenants.length > 0 && (
        <Card className="border-zinc-200">
          <CardContent className="p-4">
            <div className="font-semibold text-sm mb-3 flex items-center gap-1.5">
              <Building2 className="h-4 w-4 text-zinc-500" />
              Per-tenant breakdown
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Xero tenant</TableHead>
                  <TableHead className="text-right">GST</TableHead>
                  <TableHead className="text-right">PAYG-W</TableHead>
                  <TableHead className="text-right">PAYG-I</TableHead>
                  <TableHead className="text-right">Super</TableHead>
                  <TableHead className="text-right">FBT</TableHead>
                  <TableHead className="text-right">Co. Tax</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tenants.map((t) => {
                  const b = t.buckets || {};
                  const total = Object.values(b).reduce((s, v) => s + Number(v || 0), 0);
                  const isExp = expandedTenant === t.tenant_id;
                  return (
                    <React.Fragment key={t.tenant_id}>
                      <TableRow className="cursor-pointer hover:bg-zinc-50"
                        onClick={() => setExpandedTenant(isExp ? null : t.tenant_id)}
                        data-testid={`tax-tenant-row-${t.tenant_id}`}>
                        <TableCell className="font-medium">
                          {t.tenant_name || t.tenant_id}
                          {t.error && (
                            <div className="text-xs text-red-600 mt-1 flex items-center gap-1">
                              <AlertCircle className="h-3 w-3" />
                              {t.error}
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="text-right">{fmtMoney(b.gst)}</TableCell>
                        <TableCell className="text-right">{fmtMoney(b.payg_withholding)}</TableCell>
                        <TableCell className="text-right">{fmtMoney(b.payg_instalments)}</TableCell>
                        <TableCell className="text-right">{fmtMoney(b.super)}</TableCell>
                        <TableCell className="text-right">{fmtMoney(b.fbt)}</TableCell>
                        <TableCell className="text-right">{fmtMoney(b.company_tax)}</TableCell>
                        <TableCell className={`text-right font-semibold ${total < 0 ? "text-emerald-700" : "text-amber-700"}`}>
                          {fmtMoney(total)}
                        </TableCell>
                      </TableRow>
                      {isExp && (t.rows || []).map((r, i) => (
                        <TableRow key={`${t.tenant_id}-row-${i}`} className="bg-zinc-50/50">
                          <TableCell className="pl-8 text-sm text-zinc-600" colSpan={7}>
                            {r.name}
                          </TableCell>
                          <TableCell className="text-right text-sm text-zinc-600">
                            {fmtMoney2(r.amount)}
                          </TableCell>
                        </TableRow>
                      ))}
                      {isExp && !(t.rows && t.rows.length) && (
                        <TableRow className="bg-zinc-50/50">
                          <TableCell colSpan={8} className="pl-8 text-xs text-zinc-400 italic">
                            No matching tax-liability rows on the Balance Sheet for this tenant.
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
            <div className="text-[11px] text-zinc-500 mt-2 italic">
              Click a tenant row to see the raw Balance Sheet line items that were matched.
            </div>
          </CardContent>
        </Card>
      )}

      {tenants.length === 0 && !loading && (
        <Card className="border-dashed border-zinc-300">
          <CardContent className="p-10 text-center text-zinc-500 text-sm">
            No Xero tenants connected — connect the Xero master under <b>Xero (master)</b> to see live tax liabilities here.
          </CardContent>
        </Card>
      )}
    </div>
  );
}
